import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowRight, ShieldCheck } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { submitLead, optimizeImg } from "../lib/api";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { track, trackLead } from "../lib/analytics";
import { ChipGroup } from "../components/ChipGroup";
import { overline, inputClass, labelClass, StepHeading, BriefRow } from "../components/FlowBits";

const HERO = "https://images.unsplash.com/photo-1763506754901-fb5be9c1add7";

export default function BuyerFlow() {
  const { t, lang } = useLang();
  const b = (k) => t(`buyPage.${k}`);
  const budgets = b("budgets");
  const areas = b("areas");
  const types = b("types");
  const L = b("labels");

  const [budget, setBudget] = useState("");
  const [areaIds, setAreaIds] = useState([]);
  const [typeIds, setTypeIds] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", note: "" });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const labelOf = (list, ids) => list.filter((o) => ids.includes(o.id)).map((o) => o.label);
  const budgetLabel = useMemo(() => budgets.find((o) => o.id === budget)?.label || "", [budgets, budget]);
  const areaLabels = labelOf(areas, areaIds);
  const typeLabels = labelOf(types, typeIds);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const started = useRef(false);
  useEffect(() => { if (!started.current) { started.current = true; track("buyer_brief_start", { lead_type: "buyer", cta_location: "buyer_funnel" }); } }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!budget) {
      toast.error(b("budgetRequired"));
      document.getElementById("buy-step-budget")?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    setLoading(true);
    const message = [
      "BUYER BRIEF",
      `Budget: ${budgetLabel}`,
      `Areas: ${areaLabels.join(", ") || "-"}`,
      `Type: ${typeLabels.join(", ") || "-"}`,
      form.note.trim() ? `Note: ${form.note.trim()}` : null,
    ].filter(Boolean).join("\n");
    try {
      await submitLead({
        name: form.name, email: form.email, phone: form.phone, interest: "buy", message, source: "buyer-brief", lang,
        meta: { budget, areas: areaIds, types: typeIds },
      });
      track("buyer_brief_complete", { lead_type: "buyer", cta_location: "buyer_funnel" });
      trackLead("buyer", "buyer_funnel");
      navigate(`/${lang}/buy/thank-you`, { state: { name: form.name } });
    } catch (err) {
      toast.error(t("contact.error"));
    } finally {
      setLoading(false);
    }
  };


  return (
    <div data-testid="buy-page">
      <Seo title={b("seoTitle")} description={b("seoDesc")} />

      {/* HERO */}
      <section className="relative w-full min-h-[60vh] flex items-end vl-grain overflow-hidden">
        <img src={optimizeImg(HERO, 1920)} alt="Nice, French Riviera" fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/75 to-background/30" />
        <div className="relative px-6 md:px-12 lg:px-24 mx-auto max-w-[1600px] w-full pt-40 pb-16 md:pb-24">
          <Reveal>
            <p className={overline}>{b("overline")}</p>
            <h1 className="mt-5 font-headings text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight text-foreground max-w-3xl whitespace-pre-line">{b("title")}</h1>
            <p className="mt-8 font-body font-light text-lg text-foreground/80 max-w-2xl leading-relaxed">{b("intro")}</p>
          </Reveal>
        </div>
      </section>

      {/* FLOW */}
      <section className="py-20 md:py-28">
        <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-[1600px] grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
          <form onSubmit={handleSubmit} data-testid="buy-form" className="lg:col-span-7 space-y-20">
            <Reveal>
              <div id="buy-step-budget" data-testid="buy-step-budget" className="scroll-mt-32">
                <StepHeading n={1} title={b("budgetTitle")} hint={b("budgetHint")} />
                <ChipGroup options={budgets} value={budget} onChange={setBudget} testPrefix="budget" label={b("budgetTitle")} />
              </div>
            </Reveal>
            <Reveal>
              <div data-testid="buy-step-area">
                <StepHeading n={2} title={b("areaTitle")} hint={b("areaHint")} />
                <ChipGroup options={areas} value={areaIds} onChange={setAreaIds} multi testPrefix="area" label={b("areaTitle")} />
              </div>
            </Reveal>
            <Reveal>
              <div data-testid="buy-step-type">
                <StepHeading n={3} title={b("typeTitle")} hint={b("typeHint")} />
                <ChipGroup options={types} value={typeIds} onChange={setTypeIds} multi testPrefix="type" label={b("typeTitle")} />
              </div>
            </Reveal>
            <Reveal>
              <div data-testid="buy-step-details">
                <StepHeading n={4} title={b("formTitle")} hint={b("formIntro")} />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="buy-name" className={labelClass}>{t("contact.name")}</label>
                    <input id="buy-name" data-testid="buy-name" name="name" autoComplete="name" required value={form.name} onChange={(e) => update("name", e.target.value)} className={inputClass} />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="buy-email" className={labelClass}>{t("contact.email")}</label>
                    <input id="buy-email" data-testid="buy-email" name="email" type="email" autoComplete="email" required value={form.email} onChange={(e) => update("email", e.target.value)} className={inputClass} />
                  </div>
                  <div className="flex flex-col gap-2 sm:col-span-2">
                    <label htmlFor="buy-phone" className={labelClass}>{t("contact.phone")}</label>
                    <input id="buy-phone" data-testid="buy-phone" name="phone" type="tel" autoComplete="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} className={inputClass} />
                  </div>
                  <div className="flex flex-col gap-2 sm:col-span-2">
                    <label htmlFor="buy-note" className={labelClass}>{b("note")}</label>
                    <textarea id="buy-note" data-testid="buy-note" name="note" rows={4} value={form.note} onChange={(e) => update("note", e.target.value)} placeholder={b("notePlaceholder")} className={`${inputClass} resize-none`} />
                    <p className="font-body text-xs text-muted-foreground/80 leading-relaxed">{b("noteHelp")}</p>
                  </div>
                </div>
                <button type="submit" data-testid="buy-submit" disabled={loading} className="mt-10 inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60 px-10 py-4 uppercase tracking-[0.2em] text-xs font-body transition-all duration-300">
                  {loading ? <><Loader2 size={15} className="animate-spin" /> {t("contact.sending")}</> : <>{b("submit")} <ArrowRight size={15} /></>}
                </button>
                <p className="mt-4 font-body text-xs text-muted-foreground/70">{b("privacy")}</p>
              </div>
            </Reveal>
          </form>

          {/* LIVE BRIEF */}
          <aside className="lg:col-span-5">
            <div className="lg:sticky lg:top-28 space-y-6">
              <Reveal delay={0.1}>
                <div data-testid="buy-brief" className="border border-primary/30 bg-secondary p-8">
                  <p className={overline}>{b("summaryTitle")}</p>
                  <dl className="mt-6 space-y-4">
                    <BriefRow label={L.budget} value={budgetLabel || L.none} testId="brief-budget" />
                    <BriefRow label={L.areas} value={areaLabels.join(", ") || L.none} testId="brief-areas" />
                    <BriefRow label={L.type} value={typeLabels.join(", ") || L.none} testId="brief-type" />
                  </dl>
                </div>
              </Reveal>
              <Reveal delay={0.2}>
                <ul className="space-y-3">
                  {b("trust").map((line) => (
                    <li key={line} className="flex items-start gap-3 font-body font-light text-sm text-foreground/70">
                      <ShieldCheck size={15} className="text-primary shrink-0 mt-0.5" /> {line}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
